/**
 * QuickActionsBar - Action button pills for Flow Coach
 *
 * Displays 4 types of quick actions:
 * - Generate: AI-generate a missing entity
 * - Validate: Help validate/refine an existing entity
 * - Examples: Show examples from knowledge base
 * - Skip: Move to next phase or entity type
 *
 * Each action type has domain-colored styling.
 */

import { Sparkles, CheckCircle, Lightbulb, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

// ============================================================================
// TYPES
// ============================================================================

export interface QuickAction {
  type: 'generate' | 'validate' | 'examples' | 'skip'
  label: string
  entityType?: string
  targetLabel?: string
}

interface QuickActionsBarProps {
  actions: QuickAction[]
  onActionClick: (action: QuickAction) => void
  disabled?: boolean
}

interface QuickActionButtonProps {
  action: QuickAction
  onClick: () => void
  disabled?: boolean
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function QuickActionsBar({ actions, onActionClick, disabled }: QuickActionsBarProps) {
  if (actions.length === 0) return null

  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {actions.map((action, idx) => (
        <QuickActionButton
          key={idx}
          action={action}
          onClick={() => onActionClick(action)}
          disabled={disabled}
        />
      ))}
    </div>
  )
}

// ============================================================================
// BUTTON COMPONENT
// ============================================================================

function QuickActionButton({ action, onClick, disabled }: QuickActionButtonProps) {
  // Action type configurations
  const config = getActionConfig(action.type)

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all',
        'border hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed',
        config.bgClass,
        config.textClass,
        config.borderClass
      )}
    >
      <config.icon className="w-3.5 h-3.5" />
      {action.label}
    </button>
  )
}

// ============================================================================
// HELPERS
// ============================================================================

interface ActionConfig {
  icon: typeof Sparkles
  bgClass: string
  textClass: string
  borderClass: string
}

function getActionConfig(type: QuickAction['type']): ActionConfig {
  switch (type) {
    case 'generate':
      return {
        icon: Sparkles,
        bgClass: 'bg-solution/10',
        textClass: 'text-solution',
        borderClass: 'border-solution/30',
      }
    case 'validate':
      return {
        icon: CheckCircle,
        bgClass: 'bg-market/10',
        textClass: 'text-market',
        borderClass: 'border-market/30',
      }
    case 'examples':
      return {
        icon: Lightbulb,
        bgClass: 'bg-business/10',
        textClass: 'text-business',
        borderClass: 'border-business/30',
      }
    case 'skip':
      return {
        icon: ArrowRight,
        bgClass: 'bg-warm-100',
        textClass: 'text-warm-700',
        borderClass: 'border-warm-300',
      }
  }
}
